import { useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { EanInput } from "@/components/barcode/EanInput";
import type { ProductLookupResult } from "@/types/camping";

export interface ProductBarcodeLookupFieldProps {
  barcode: string;
  onBarcodeChange: (value: string) => void;
  onLookupResult: (result: ProductLookupResult) => void;
  disabled?: boolean;
}

export function ProductBarcodeLookupField({
  barcode,
  onBarcodeChange,
  onLookupResult,
  disabled,
}: ProductBarcodeLookupFieldProps) {
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runLookup(scannedCode?: string) {
    const code = (scannedCode ?? barcode).trim();
    if (!code) {
      setError("Bitte Barcode eingeben oder scannen.");
      return;
    }
    setLoading(true);
    setError(null);
    setHint(null);
    try {
      const result = await api.get<ProductLookupResult>(
        `/api/v1/articles/lookup?barcode=${encodeURIComponent(code)}`,
      );
      onLookupResult(result);
      if (result.found) {
        setHint(
          result.source === "open_food_facts"
            ? "Daten aus Open Food Facts übernommen."
            : "Produktdaten gefunden.",
        );
      } else {
        setHint(
          `Barcode ${result.barcode}: kein Produkt in Open Food Facts gefunden – bitte Bezeichnung und Preis manuell eingeben.`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Suche fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-2">
      <label className="flex flex-col gap-1 text-sm">
        Barcode (EAN/GTIN)
        <div className="flex gap-2">
          <EanInput
            value={barcode}
            onChange={(value) => {
              onBarcodeChange(value);
              setHint(null);
              setError(null);
            }}
            onEnter={(code) => void runLookup(code)}
            disabled={disabled || loading}
            cameraOnDesktop
          />
          <Button
            type="button"
            variant="secondary"
            className="shrink-0 min-h-[44px]"
            loading={loading}
            disabled={disabled}
            onClick={() => void runLookup()}
          >
            Suchen
          </Button>
        </div>
      </label>
      <p className="text-xs text-label-secondary">
        Öffentliche Datenbank (Open Food Facts) – Lebensmittel und Getränke.
      </p>
      {hint ? <p className="text-xs text-label-secondary">{hint}</p> : null}
      {error ? <p className="text-xs text-status-red">{error}</p> : null}
    </div>
  );
}
